/**
 * OWL2 Property Expression Types
 *
 * Defines object property expressions and data property expressions.
 * Object property expressions can be either a named property or an inverse.
 * Data property expressions are always named properties.
 */

import type { IRI } from './IRI.ts';

// =============================================================================
// Object Property Expression Types
// =============================================================================

/**
 * Named object property - references a property by IRI.
 */
export interface ObjectProperty {
    readonly type: 'ObjectProperty';
    readonly iri: IRI;
}

/**
 * Inverse of an object property.
 * OWL2: ObjectInverseOf(P) means the inverse of property P.
 */
export interface ObjectInverseOf {
    readonly type: 'ObjectInverseOf';
    readonly property: ObjectProperty;
}

/**
 * Discriminated union of object property expression types.
 * An object property expression can be either a named property or its inverse.
 */
export type ObjectPropertyExpression = ObjectProperty | ObjectInverseOf;

// =============================================================================
// Data Property Expression Types
// =============================================================================

/**
 * Named data property - references a property by IRI.
 * In OWL2, data property expressions are always named properties
 * (there is no "inverse" for data properties).
 */
export interface DataProperty {
    readonly type: 'DataProperty';
    readonly iri: IRI;
}

/**
 * Data property expression type.
 * Currently just an alias for DataProperty, but defined as a type
 * for consistency with the OWL2 specification.
 */
export type DataPropertyExpression = DataProperty;

// =============================================================================
// Object Property Chain
// =============================================================================

/**
 * Object property chain - a sequence of object property expressions.
 * Used in SubObjectPropertyOf axioms to express property chains.
 * OWL2: ObjectPropertyChain(P1 ... Pn)
 *
 * Example: hasParent o hasBrother implies hasUncle
 */
export interface ObjectPropertyChain {
    readonly type: 'ObjectPropertyChain';
    /** At least two properties in the chain */
    readonly properties: readonly [ObjectPropertyExpression, ObjectPropertyExpression, ...ObjectPropertyExpression[]];
}

/**
 * Sub-property expression - can be either a single property expression or a chain.
 * Used as the "sub" part of SubObjectPropertyOf axioms.
 */
export type SubObjectPropertyExpression = ObjectPropertyExpression | ObjectPropertyChain;

// =============================================================================
// Factory Functions
// =============================================================================

/**
 * Create a named object property.
 */
export function objectProperty(iri: IRI): ObjectProperty {
    return { type: 'ObjectProperty', iri };
}

/**
 * Create an inverse object property expression.
 */
export function objectInverseOf(property: ObjectProperty): ObjectInverseOf {
    return { type: 'ObjectInverseOf', property };
}

/**
 * Create a named data property.
 */
export function dataProperty(iri: IRI): DataProperty {
    return { type: 'DataProperty', iri };
}

/**
 * Create an object property chain.
 * Requires at least 2 properties.
 */
export function objectPropertyChain(
    first: ObjectPropertyExpression,
    second: ObjectPropertyExpression,
    ...rest: ObjectPropertyExpression[]
): ObjectPropertyChain {
    return {
        type: 'ObjectPropertyChain',
        properties: [first, second, ...rest]
    };
}

// =============================================================================
// Type Guards
// =============================================================================

/**
 * Check if an object property expression is a named property.
 */
export function isObjectProperty(expr: ObjectPropertyExpression): expr is ObjectProperty {
    return expr.type === 'ObjectProperty';
}

/**
 * Check if an object property expression is an inverse.
 */
export function isObjectInverseOf(expr: ObjectPropertyExpression): expr is ObjectInverseOf {
    return expr.type === 'ObjectInverseOf';
}

/**
 * Check if a sub-property expression is a property chain.
 */
export function isObjectPropertyChain(expr: SubObjectPropertyExpression): expr is ObjectPropertyChain {
    return expr.type === 'ObjectPropertyChain';
}

/**
 * Check if a sub-property expression is a single property expression.
 */
export function isObjectPropertyExpression(expr: SubObjectPropertyExpression): expr is ObjectPropertyExpression {
    return expr.type === 'ObjectProperty' || expr.type === 'ObjectInverseOf';
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Get the IRI of a property expression.
 * For ObjectInverseOf, returns the IRI of the wrapped property.
 */
export function getPropertyIRI(expr: ObjectPropertyExpression | DataProperty): IRI {
    switch (expr.type) {
        case 'ObjectProperty':
        case 'DataProperty':
            return expr.iri;
        case 'ObjectInverseOf':
            return expr.property.iri;
    }
}

/**
 * Get all property IRIs from a sub-property expression.
 * For property chains, returns all IRIs in the chain.
 */
export function getAllPropertyIRIs(expr: SubObjectPropertyExpression): IRI[] {
    if (isObjectPropertyChain(expr)) {
        return expr.properties.map(getPropertyIRI);
    }
    return [getPropertyIRI(expr)];
}
